import React, { useEffect, useState } from "react";
import { adminService } from "../../services/admin.service";
import "./AdminOrdersPage.css";

// Helper function to format order IDs
const formatOrderId = (id) => {
  if (!id) return "";
  return "OD" + String(id).padStart(4, "0");
};

const statusBadgeClass = (status) => {
  if (!status) return "badge";
  const s = status.toLowerCase();
  if (s.includes("paid") || s.includes("delivered"))
    return "badge badge-success";
  if (s.includes("pending")) return "badge badge-warning";
  if (s.includes("cancel")) return "badge badge-danger";
  if (s.includes("shipped")) return "badge badge-info";
  return "badge";
};

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");

  // State for Manual Tracking Entry
  const [trackingInput, setTrackingInput] = useState({ awb: "", courier: "" });

  // ✅ NEW: State for Live Sync
  const [isSyncing, setIsSyncing] = useState(false);

  // State for Search and Filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  // Load Orders
  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await adminService.getOrders();
      setOrders(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  // Open Modal
  const openOrderDetails = async (orderId) => {
    try {
      setTrackingInput({ awb: "", courier: "" });

      const order = await adminService.getOrderById(orderId);
      setSelectedOrder(order);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      alert("Failed to load order details");
    }
  };

  // Update Status Manually
  const handleStatusChange = async (orderId, newStatus) => {
    if (!window.confirm(`Update order to ${newStatus}?`)) return;

    try {
      setUpdatingId(orderId);
      await adminService.updateOrderStatus(orderId, { status: newStatus });
      await loadOrders();

      const updated = await adminService.getOrderById(orderId);
      setSelectedOrder(updated);
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  // Handle Saving Tracking Info
  const handleSaveTracking = async (orderId) => {
    if (!trackingInput.awb) {
      return alert("Please enter the AWB tracking number.");
    }

    try {
      const carrierName = trackingInput.courier || "Ekart Logistics";
      const trackingUrl = carrierName.toLowerCase().includes("shiprocket")
        ? `https://shiprocket.co/tracking/${trackingInput.awb}`
        : `https://ekartlogistics.com/shipmenttrack/${trackingInput.awb}`;

      const payload = {
        status: "Shipped",
        tracking_number: trackingInput.awb,
        carrier_name: carrierName,
        tracking_url: trackingUrl,
      };

      await adminService.updateOrderStatus(orderId, payload);

      const updatedOrder = await adminService.getOrderById(orderId);
      setSelectedOrder(updatedOrder);
      await loadOrders();
      alert("Tracking information saved! Order is now marked as Shipped.");
    } catch (err) {
      console.error(err);
      alert("Failed to save tracking information.");
    }
  };

  // ✅ NEW: Handle Live Sync & Auto-Update to Delivered
  const handleSyncLiveStatus = async (order) => {
    if (!order.tracking_number) return;

    try {
      setIsSyncing(true);
      const courier = (order.carrier_name || "").toLowerCase();
      let liveStatus = "Status Unchanged";
      let isDelivered = false;

      // Check Ekart directly via proxy
      if (courier.includes("ekart")) {
        const targetUrl = encodeURIComponent(
          `https://app.elite.ekartlogistics.in/api/v1/track/${order.tracking_number}`,
        );
        const response = await fetch(`https://corsproxy.io/?${targetUrl}`);

        if (response.ok) {
          const trackData = await response.json();
          if (trackData?.track) {
            liveStatus =
              trackData.track.status || trackData.track.desc || "Unknown";

            // Auto-detect delivery
            if (liveStatus.toLowerCase().includes("delivered")) {
              isDelivered = true;
            }
          }
        } else {
          alert("Could not connect to Ekart API.");
          return;
        }
      } else {
        alert("Auto-sync is currently only supported for Ekart Logistics.");
        return;
      }

      // If delivered on Ekart, update our DB
      if (isDelivered && order.status !== "Delivered") {
        await adminService.updateOrderStatus(order.order_id, {
          status: "Delivered",
        });
        await loadOrders();
        const updated = await adminService.getOrderById(order.order_id);
        setSelectedOrder(updated);
        alert(
          `Success! Package is delivered. Order status has been automatically updated to "Delivered".`,
        );
      } else {
        alert(`Current Live Logistics Status: ${liveStatus}`);
      }
    } catch (error) {
      console.error(error);
      alert("Failed to sync live tracking data.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Filter Logic
  const filteredOrders = orders.filter((o) => {
    if (filterStatus !== "All" && o.status !== filterStatus) return false;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const orderIdStr = String(o.order_id);
      const formattedIdStr = formatOrderId(o.order_id).toLowerCase();
      const name = (o.shipping_name || "").toLowerCase();
      const email = (o.User?.email || "").toLowerCase();
      const city = (o.shipping_city || "").toLowerCase();

      if (
        !orderIdStr.includes(term) &&
        !formattedIdStr.includes(term) &&
        !name.includes(term) &&
        !email.includes(term) &&
        !city.includes(term)
      ) {
        return false;
      }
    }
    return true;
  });

  if (loading) return <div className="p-4">Loading orders...</div>;

  return (
    <div className="admin-orders-page">
      <div className="admin-header">
        <h2>Order Management</h2>
        <button onClick={loadOrders} className="admin-btn secondary">
          Refresh
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {/* ================= FILTER BAR ================= */}
      <div className="orders-filter-bar">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by Order #, Name, Email, or City..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-wrapper">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="All">All Statuses</option>
            <option value="Pending Payment">Pending Payment</option>
            <option value="Paid">Paid</option>
            <option value="Processing">Processing</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Date</th>
              <th>Customer</th>
              <th>City</th>
              <th>Total (₹)</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td
                  colSpan="6"
                  className="no-data"
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No orders found.
                </td>
              </tr>
            ) : (
              filteredOrders.map((o) => (
                <tr key={o.order_id}>
                  <td>
                    <button
                      className="order-link"
                      onClick={() => openOrderDetails(o.order_id)}
                    >
                      {formatOrderId(o.order_id)}
                    </button>
                  </td>
                  <td>
                    {new Date(o.createdAt).toLocaleDateString()} <br />
                    <small>
                      {new Date(o.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </small>
                  </td>
                  <td>
                    <strong>{o.shipping_name}</strong>
                    <br />
                    <small>{o.User?.email}</small>
                  </td>
                  <td>{o.shipping_city}</td>
                  <td className="amount">
                    ₹{parseFloat(o.total_amount).toLocaleString("en-IN")}
                  </td>
                  <td>
                    <span className={statusBadgeClass(o.status)}>
                      {o.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ================= MODAL ================= */}

      {showModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Order {formatOrderId(selectedOrder.order_id)}</h3>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "18px",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Customer Info */}
              <div className="section">
                <h4>Customer Details</h4>
                <p>
                  <strong>Name:</strong>{" "}
                  {selectedOrder.User?.first_name &&
                  selectedOrder.User?.last_name
                    ? `${selectedOrder.User.first_name} ${selectedOrder.User.last_name}`
                    : selectedOrder.User?.full_name ||
                      selectedOrder.shipping_name}
                </p>
                <p>
                  <strong>Email:</strong> {selectedOrder.User?.email}
                </p>
                <p>
                  <strong>Phone:</strong>{" "}
                  {selectedOrder.shipping_phone ||
                    selectedOrder.User?.phone_number ||
                    "N/A"}
                </p>
              </div>

              {/* Payment & Refund Information */}
              <div className="section">
                <h4>Payment Information</h4>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "16px",
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div>
                    <p style={{ fontSize: "1.1rem", margin: "0 0 8px 0" }}>
                      <strong>Total Amount:</strong> ₹
                      {parseFloat(selectedOrder.total_amount).toLocaleString(
                        "en-IN",
                      )}
                    </p>
                    <p style={{ margin: 0 }}>
                      <strong>Current Status:</strong>{" "}
                      <span className={statusBadgeClass(selectedOrder.status)}>
                        {selectedOrder.status}
                      </span>
                    </p>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "11px",
                        color: "#64748b",
                        textTransform: "uppercase",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Payment Method
                    </p>
                    {selectedOrder.payment_method === "COD" ||
                    selectedOrder.status?.toUpperCase() === "COD" ? (
                      <span
                        className="badge badge-cod"
                        style={{ fontSize: "13px", padding: "6px 12px" }}
                      >
                        💵 Cash on Delivery (COD)
                      </span>
                    ) : (
                      <span
                        className="badge badge-online"
                        style={{ fontSize: "13px", padding: "6px 12px" }}
                      >
                        💳 Prepaid (Online)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="section">
                <h4>Shipping Address</h4>
                <div className="address-box">
                  <p>{selectedOrder.shipping_line1}</p>
                  {selectedOrder.shipping_line2 && (
                    <p>{selectedOrder.shipping_line2}</p>
                  )}
                  <p>
                    <strong>{selectedOrder.shipping_city}</strong>
                    {selectedOrder.shipping_state && (
                      <>
                        {" "}
                        - <strong>{selectedOrder.shipping_state}</strong>
                      </>
                    )}
                  </p>
                  <p>
                    <strong>Pincode:</strong> {selectedOrder.shipping_pincode}
                  </p>
                </div>
              </div>

              {/* Items */}
              <div className="section">
                <h4>Items Ordered</h4>
                {selectedOrder.OrderItems?.map((item, idx) => (
                  <div key={idx} className="order-item">
                    <img
                      src={item.Inventory?.image_url || "/fallback.png"}
                      alt={item.Inventory?.name}
                    />
                    <div className="item-details">
                      <div className="item-name">{item.Inventory?.name}</div>
                      {item.Inventory?.sku && (
                        <div className="item-sku">
                          <strong>SKU:</strong> {item.Inventory.sku}
                        </div>
                      )}
                      <div className="item-quantity">
                        Quantity: {item.quantity} × ₹
                        {parseFloat(item.unit_price).toLocaleString("en-IN")}
                      </div>
                      <div className="item-total">
                        Total: ₹
                        {parseFloat(
                          item.quantity * item.unit_price,
                        ).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ================= LOGISTICS (EKART INTEGRATION) ================= */}
              <div className="section logistics-section">
                <h4>Fulfillment & Logistics</h4>

                {selectedOrder.status === "Cancelled" ? (
                  <div
                    style={{
                      padding: "15px",
                      backgroundColor: "#fee2e2",
                      border: "1px solid #f87171",
                      borderRadius: "6px",
                      color: "#991b1b",
                    }}
                  >
                    <p style={{ margin: 0 }}>
                      <strong>🚫 This order has been cancelled.</strong>
                    </p>
                  </div>
                ) : selectedOrder.tracking_number ? (
                  <div className="tracking-info-box">
                    <div className="tracking-details">
                      <p>
                        <strong>Courier:</strong>{" "}
                        {selectedOrder.carrier_name || "Ekart Logistics"}
                      </p>
                      <p>
                        <strong>AWB Number:</strong>{" "}
                        <span style={{ color: "#2563eb", fontWeight: "bold" }}>
                          {selectedOrder.tracking_number}
                        </span>
                      </p>
                    </div>
                    <div
                      className="tracking-actions"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        minWidth: "140px",
                      }}
                    >
                      <a
                        href={selectedOrder.tracking_url}
                        target="_blank"
                        rel="noreferrer"
                        className="admin-btn ghost"
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1px solid #cbd5e1",
                          textAlign: "center",
                          textDecoration: "none",
                          color: "#475569",
                          fontSize: "13px",
                          borderRadius: "6px",
                        }}
                      >
                        View Live Map ↗
                      </a>

                      {/* ✅ NEW: Sync Live Status Button */}
                      <button
                        onClick={() => handleSyncLiveStatus(selectedOrder)}
                        disabled={isSyncing}
                        style={{
                          width: "100%",
                          padding: "10px",
                          background: "#e0f2fe",
                          color: "#0284c7",
                          border: "1px solid #bae6fd",
                          borderRadius: "6px",
                          cursor: isSyncing ? "not-allowed" : "pointer",
                          fontWeight: "600",
                          fontSize: "13px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          opacity: isSyncing ? 0.7 : 1,
                        }}
                      >
                        {isSyncing ? "Checking..." : "🔄 Sync Live Status"}
                      </button>
                    </div>
                  </div>
                ) : selectedOrder.status === "Processing" ? (
                  <div
                    style={{
                      padding: "15px",
                      backgroundColor: "#f8fafc",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 10px 0",
                        color: "#334155",
                        fontWeight: "600",
                      }}
                    >
                      📦 Enter Tracking Details
                    </p>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "15px",
                      }}
                    >
                      Generate the AWB in your Ekart dashboard, then paste it
                      here. Saving will automatically mark the order as{" "}
                      <strong>Shipped</strong>.
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="AWB Tracking Number (Required)"
                        value={trackingInput.awb}
                        onChange={(e) =>
                          setTrackingInput({
                            ...trackingInput,
                            awb: e.target.value,
                          })
                        }
                        className="admin-input"
                      />
                      <input
                        type="text"
                        placeholder="Courier Name (Defaults to Ekart Logistics)"
                        value={trackingInput.courier}
                        onChange={(e) =>
                          setTrackingInput({
                            ...trackingInput,
                            courier: e.target.value,
                          })
                        }
                        className="admin-input"
                      />
                      <button
                        className="admin-btn primary"
                        onClick={() =>
                          handleSaveTracking(selectedOrder.order_id)
                        }
                        style={{ marginTop: "5px" }}
                      >
                        Save Tracking & Mark as Shipped
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="generate-awb-box">
                    <p style={{ color: "#666", fontSize: "13px" }}>
                      Change the status to <strong>Processing</strong> to input
                      shipping tracking numbers.
                    </p>
                  </div>
                )}
              </div>

              {/* Status Update (Manual Override) */}
              <div className="section">
                <h4>Manual Status Override</h4>
                <select
                  value={selectedOrder.status}
                  disabled={updatingId === selectedOrder.order_id}
                  onChange={(e) =>
                    handleStatusChange(selectedOrder.order_id, e.target.value)
                  }
                  style={{
                    padding: "8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    width: "100%",
                  }}
                >
                  <option value="Pending Payment">Pending Payment</option>
                  <option value="Paid">Paid</option>
                  <option value="Processing">Processing</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                {updatingId === selectedOrder.order_id && (
                  <p style={{ color: "blue", marginTop: "8px" }}>Updating...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;
